/**
 * SeasonManagerService.ts
 * Handles league season initialization and management
 * 
 * Day 0 → Day 1:
 * - Check all leagues have exactly 20 teams
 * - Create AI teams for incomplete leagues
 * - Generate schedules for all ready leagues
 * - Assign conferences/divisions
 * 
 * Day 7:
 * - Auto-fill rosters to 25 players (15 position, 10 pitchers)
 */

import { supabase, isSupabaseConfigured } from './supabase';
import { generateSeasonSchedule, generateAITeam, TeamScheduleInfo, assignTeamsToConferences } from '../engine/ScheduleGenerator';
import { generatePlayoffBracket } from '../engine/PlayoffGenerator';

export interface LeagueInfo {
  id: string;
  name: string;
  tier: number;
  status: string;
}

export interface TeamInfo {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  division: string;
  conference: string;
  is_ai: boolean;
  league_id: string;
}

const REQUIRED_TEAMS = 24;
const ROSTER_SIZE = 25;
const POSITION_PLAYERS = 15;
const PITCHERS = 10;

// Get all leagues
export async function getAllLeagues(): Promise<LeagueInfo[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data } = await supabase.from('leagues').select('*').order('tier');
    return data || [];
  }
  return [];
}

// Get all teams in a league
export async function getTeamsInLeague(leagueId: string): Promise<TeamInfo[]> {
  if (isSupabaseConfigured() && supabase) {
    const { data } = await supabase.from('teams').select('*').eq('league_id', leagueId).order('name');
    return data || [];
  }
  return [];
}

// Count teams in a league
export async function countTeamsInLeague(leagueId: string): Promise<number> {
  const teams = await getTeamsInLeague(leagueId);
  return teams.length;
}

// Create an AI team and add to league
export async function createAITeamForLeague(league: LeagueInfo): Promise<TeamInfo> {
  const teamData = generateAITeam(league.tier);

  // Assign a random AI owner personality with slight variance so every team is unique
  const { randomAIArchetype, getAIStrategy } = await import('../engine/AIManager');
  const archetype = randomAIArchetype();
  const aiStrategy = getAIStrategy(archetype, true);
  
  const newTeam = {
    name: teamData.name,
    city: teamData.city,
    abbreviation: teamData.abbreviation,
    tier: league.tier,
    division: 'East', // Will be reassigned properly later
    conference: 'American',
    is_ai: true,
    ai_archetype: archetype,  // store so we can display it
    strategy: aiStrategy,     // personality baked in from creation
    league_id: league.id,
    wins: 0,
    losses: 0
  };
  
  if (isSupabaseConfigured() && supabase) {
    const { data, error } = await supabase.from('teams').insert(newTeam).select().single();
    if (error) throw error;
    console.log(`🤖 Created AI team: ${teamData.city} ${teamData.name}`);
    return data;
  }
  
  // Fallback: return mock (shouldn't happen in practice)
  return { ...newTeam, id: `ai-${Date.now()}` } as TeamInfo;
}

// Generate schedule for a league
export async function generateLeagueSchedule(league: LeagueInfo): Promise<void> {
  const teams = await getTeamsInLeague(league.id);
  
  if (teams.length !== REQUIRED_TEAMS) {
    console.warn(`⚠️ League ${league.name} has ${teams.length} teams, needs ${REQUIRED_TEAMS}`);
    return;
  }
  
  // Assign conferences and divisions
  // Generate schedule
  console.log(`🔍 Teams from database:`, teams.slice(0, 3));
  console.log(`🔍 Total teams: ${teams.length}`);
  
  // Assign conferences and divisions
  const teamAssignments = assignTeamsToConferences(teams as any);
  
  console.log(`🔍 After assignTeamsToConferences - teamAssignments:`, teamAssignments);
  console.log(`🔍 TeamAssignments count: ${teamAssignments.length}, League ID: ${league.id}`);
  
  const games = generateSeasonSchedule(teamAssignments, league?.id || "");
  
  console.log(`📅 Generated schedule for ${league.name}: ${games.length} games`);
  if (games.length > 0) {
    console.log(`🔍 First game from generator:`, games[0]);
  }
  
  // Save to database - clear old schedule first
  if (isSupabaseConfigured() && supabase) {
    // Delete old schedule for this league
    await supabase.from('league_schedule').delete().eq('league_id', league.id);
    
    // Insert new games - FILTER OUT games with undefined/null team IDs
    const validGames = games.filter(g => g.homeTeamId && g.awayTeamId);
    const invalidGames = games.filter(g => !g.homeTeamId || !g.awayTeamId);
    
    if (invalidGames.length > 0) {
      console.warn(`⚠️ Filtered out ${invalidGames.length} games with undefined team IDs`);
      console.warn('⚠️ First invalid game:', invalidGames[0]);
    }
    
    const gameRecords = validGames.map(g => ({
      league_id: league.id,
      game_id: g.id,
      series_id: g.seriesId || null,
      home_team_id: g.homeTeamId,
      away_team_id: g.awayTeamId,
      game_day: g.gameDay,
      game_number: g.gameNumber,
      status: 'scheduled',
      game_date: g.gameDay,
    }));
    
    if (gameRecords.length > 0) {
      const { error } = await supabase.from('league_schedule').insert(gameRecords);
      if (error) {
        console.error('❌ Schedule insertion failed:', error);
        console.error('❌ First few gameRecords:', gameRecords.slice(0, 3));
        return;
      }
    }
    
    // Update team conference/division assignments
    for (const assignment of teamAssignments) {
      await supabase.from('teams').update({
        conference: assignment.conference,
        division: assignment.division
      }).eq('id', assignment.teamId);
    }

    // Also sync conference/division into league_standings rows
    // (standings may have been created before assignments were finalized)
    for (const assignment of teamAssignments) {
      await supabase.from('league_standings').update({
        conference: assignment.conference,
        division: assignment.division
      }).eq('team_id', assignment.teamId).eq('league_id', league.id);
    }
    
    console.log(`✅ Saved schedule to database for ${league.name}`);
  }
}

// Main function: Advance from Day 0 to Day 1
export async function advanceToDayOne(): Promise<{ success: boolean; message: string; leaguesReady: number; leaguesSkipped: number }> {
  const leagues = await getAllLeagues();
  let leaguesReady = 0;
  let leaguesSkipped = 0;
  
  console.log(`🎯 Checking ${leagues.length} leagues for Day 1 advance...`);
  
  if (leagues.length === 0) {
    console.log('⚠️ No leagues found! Teams need to be assigned to leagues first.');
    return { success: false, message: '❌ No leagues found. Create a league and assign teams first.', leaguesReady: 0, leaguesSkipped: 0 };
  }
  
  for (const league of leagues) {
    let teamCount = await countTeamsInLeague(league.id);
    console.log(`📋 League "${league.name}": ${teamCount} teams (need ${REQUIRED_TEAMS})`);
    
    if (teamCount < REQUIRED_TEAMS) {
      // Need to create AI teams
      const needed = REQUIRED_TEAMS - teamCount;
      console.log(`🤖 League ${league.name} has ${teamCount} teams, creating ${needed} AI teams...`);
      
      for (let i = 0; i < needed; i++) {
        await createAITeamForLeague(league);
      }
      
      // Recount after creating AI teams
      teamCount = await countTeamsInLeague(league.id);
      console.log(`📋 After AI fill: ${teamCount} teams in ${league.name}`);
    }
    
    if (teamCount > REQUIRED_TEAMS) {
      console.warn(`⚠️ League ${league.name} has ${teamCount} teams (too many), skipping...`);
      leaguesSkipped++;
      continue;
    }
    
    if (teamCount < REQUIRED_TEAMS) {
      console.warn(`⚠️ League ${league.name} still has ${teamCount} teams after AI fill, skipping...`);
      leaguesSkipped++;
      continue;
    }
    
    // Generate schedule
    console.log(`📅 Generating schedule for ${league.name} (${teamCount} teams)...`);
    await generateLeagueSchedule(league);
    
    // Create initial standings
    console.log(`📊 Creating standings for ${league.name}...`);
    await createLeagueStandings(league.id);
    
    // Verify standings were created
    if (isSupabaseConfigured() && supabase) {
      const { count: standingsCount } = await supabase.from('league_standings').select('*', { count: 'exact', head: true }).eq('league_id', league.id);
      console.log(`📊 Standings created: ${standingsCount} entries`);
    }
    
    // Create playoff bracket (pending until regular season ends)
    await createPlayoffBracket(league);
    
    leaguesReady++;
    console.log(`✅ ${league.name} ready!`);
  }
  
    // Ensure all active leagues have sim_speed set so the Cloudflare Worker picks them up
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('leagues')
      .update({ sim_speed: 5 })
      .eq('status', 'active')
      .is('sim_speed', null);
  }

  await updateSimulationDay(1);
  
  return {
    success: true,
    message: `Advanced to Day 1. ${leaguesReady} leagues ready${leaguesSkipped > 0 ? `, ${leaguesSkipped} skipped (check console)` : ''}.`,
    leaguesReady,
    leaguesSkipped
  };
}

// Create initial standings for a league
async function createLeagueStandings(leagueId: string): Promise<void> {
  const teams = await getTeamsInLeague(leagueId);
  
  if (isSupabaseConfigured() && supabase) {
    // Clear old standings
    await supabase.from('league_standings').delete().eq('league_id', leagueId);
    
    // Create new standings
    const standings = teams.map((team, idx) => ({
      id: `stand_${leagueId}_${team.id}`,
      league_id: leagueId,
      team_id: team.id,
      wins: 0,
      losses: 0,
      runs_scored: 0,
      runs_allowed: 0,
      division: team.division,
      conference: team.conference
    }));
    
    await supabase.from('league_standings').insert(standings);
  }
}

// Create playoff bracket for a league
async function createPlayoffBracket(league: LeagueInfo): Promise<void> {
  const teams = await getTeamsInLeague(league.id);
  
  // Get top 2 from each division
  const divisions = ['American-East', 'American-West', 'National-East', 'National-West'];
  const playoffTeams: any[] = [];
  
  // For now, just pick any 8 teams (will be replaced with actual standings)
  const sortedTeams = [...teams].slice(0, 8);
  
  const standings = sortedTeams.map(t => ({
    teamId: t.id,
    teamName: `${t.city} ${t.name}`,
    division: `${t.conference}-${t.division}`,
    wins: 0,
    losses: 0,
    winPct: 0
  }));
  
  const bracket = generatePlayoffBracket(league.id, new Date().getFullYear(), standings, 161); // Starts day 161
  
  if (isSupabaseConfigured() && supabase) {
    // Clear old playoffs
    await supabase.from('playoff_brackets').delete().eq('league_id', league.id);
    
    // Insert new bracket
    await supabase.from('playoff_brackets').insert({
      id: bracket.id,
      league_id: league.id,
      season_year: bracket.seasonYear,
      status: 'pending',
      champion: null,
      start_day: bracket.startDay,
      end_day: null
    });
  }
  
  console.log(`🏆 Created playoff bracket for ${league.name}`);
}

// Update simulation day
async function updateSimulationDay(day: number): Promise<void> {
  if (isSupabaseConfigured() && supabase) {
    await supabase.from('simulation_state').upsert({
      id: 'global',
      current_day: day,
      is_running: day > 0,
      last_updated: new Date().toISOString()
    });
  }
}

// Check and fill rosters on Day 7
export async function checkAndFillRosters(): Promise<{ filled: number; teamsAffected: number }> {
  const leagues = await getAllLeagues();
  let filled = 0;
  let teamsAffected = 0;
  
  console.log(`📋 Checking rosters for ${leagues.length} leagues...`);
  
  for (const league of leagues) {
    const teams = await getTeamsInLeague(league.id);
    console.log(`📋 League "${league.name}": ${teams.length} teams`);
    
    for (const team of teams) {
      const rosterCount = await countPlayersOnTeam(team.id);
      const teamDisplayName = team.name.startsWith(team.city) ? team.name : `${team.city} ${team.name}`;
      console.log(`  ${teamDisplayName}: ${rosterCount} players (need ${ROSTER_SIZE})`);
      
      if (rosterCount < ROSTER_SIZE) {
        const needed = ROSTER_SIZE - rosterCount;
        const positionNeeded = Math.min(needed, POSITION_PLAYERS - Math.min(await countPositionPlayers(team.id), POSITION_PLAYERS));
        const pitcherNeeded = needed - positionNeeded;
        
        console.log(`  → Need ${positionNeeded} position players + ${pitcherNeeded} pitchers`);
        
        // Generate position players
        for (let i = 0; i < positionNeeded; i++) {
          await generatePlayerForTeam(team.id, false);
          filled++;
        }
        
        // Generate pitchers
        for (let i = 0; i < pitcherNeeded; i++) {
          await generatePlayerForTeam(team.id, true);
          filled++;
        }
        
        teamsAffected++;
      }
    }
  }
  
  console.log(`📋 Roster fill complete: ${filled} players added to ${teamsAffected} teams`);
  return { filled, teamsAffected };
}

// Count players on a team
async function countPlayersOnTeam(teamId: string): Promise<number> {
  if (isSupabaseConfigured() && supabase) {
    const { count } = await supabase.from('players').select('*', { count: 'exact', head: true }).eq('team_id', teamId);
    return count || 0;
  }
  return 0;
}

// Count position players on a team
async function countPositionPlayers(teamId: string): Promise<number> {
  if (isSupabaseConfigured() && supabase) {
    const { count } = await supabase.from('players')
      .select('*', { count: 'exact', head: true })
      .eq('team_id', teamId)
      .eq('is_hitter', true);
    return count || 0;
  }
  return 0;
}

// Generate a player for a team
// Portrait helpers
const FACE_SHAPES = ['oval', 'round', 'square', 'long', 'heart'];
const SKIN_TONES = ['skin_light', 'skin_tan', 'skin_medium', 'skin_brown', 'skin_dark'];
const EYE_STYLES = ['oval', 'dots', 'angry', 'sleepy', 'wide'];
const EYE_COLORS = ['brown', 'blue', 'green', 'hazel', 'gray', 'amber'];
const EYE_SIZES = [1, 2, 3];
const MOUTH_STYLES = ['smile', 'frown', 'open', 'grin', 'tongue', 'neutral'];
const MOUTH_COLORS = ['red', 'pink', 'brown', 'green', 'black'];
const HAIR_STYLES = ['short', 'afro', 'mohawk', 'ponytail', 'buzz', 'long', 'spiky', 'none'];
const HAIR_COLORS = ['black', 'brown_dark', 'brown', 'blonde', 'red_hair', 'gray', 'white'];
const FACIAL_HAIR_STYLES = ['none', 'none', 'none', 'stubble', 'mustache', 'goatee', 'beard'];
const FACIAL_HAIR_COLORS = ['black', 'brown_dark', 'brown', 'blonde', 'red_hair', 'gray'];

function generatePortrait() {
  return {
    bg_color: SKIN_TONES[Math.floor(Math.random() * SKIN_TONES.length)],
    eye_style: EYE_STYLES[Math.floor(Math.random() * EYE_STYLES.length)],
    eye_color: EYE_COLORS[Math.floor(Math.random() * EYE_COLORS.length)],
    eye_size: EYE_SIZES[Math.floor(Math.random() * EYE_SIZES.length)],
    mouth_style: MOUTH_STYLES[Math.floor(Math.random() * MOUTH_STYLES.length)],
    mouth_color: MOUTH_COLORS[Math.floor(Math.random() * MOUTH_COLORS.length)],
    hair_style: HAIR_STYLES[Math.floor(Math.random() * HAIR_STYLES.length)],
    hair_color: HAIR_COLORS[Math.floor(Math.random() * HAIR_COLORS.length)],
    facial_hair_style: FACIAL_HAIR_STYLES[Math.floor(Math.random() * FACIAL_HAIR_STYLES.length)],
    facial_hair_color: FACIAL_HAIR_COLORS[Math.floor(Math.random() * FACIAL_HAIR_COLORS.length)],
  };
}

async function generatePlayerForTeam(teamId: string, isPitcher: boolean): Promise<void> {
  const positions = isPitcher 
    ? ['P'] 
    : ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];
  
  const position = positions[Math.floor(Math.random() * positions.length)];
  
  // Generate random attributes as individual columns
  const overall = Math.floor(50 + Math.random() * 40);
  
  const playerData: any = {
    team_id: teamId,
    first_name: getRandomFirstName(),
    last_name: getRandomLastName(),
    position,
    is_hitter: !isPitcher,
    retired: false,
    batting_hand: Math.random() > 0.5 ? 'L' : 'R',
    throwing_hand: Math.random() > 0.5 ? 'R' : 'L',
    height: Math.floor(68 + Math.random() * 12),
    weight: Math.floor(160 + Math.random() * 80),
    age: Math.floor(22 + Math.random() * 15),
    overall,
    endurance: Math.floor(50 + Math.random() * 40),
    ...generatePortrait(),
  };
  
  if (!isPitcher) {
    playerData.contact = Math.floor(40 + Math.random() * 50);
    playerData.power = Math.floor(40 + Math.random() * 50);
    playerData.speed = Math.floor(30 + Math.random() * 50);
    playerData.discipline = Math.floor(40 + Math.random() * 50);
    playerData.arm = Math.floor(40 + Math.random() * 50);
    playerData.fielding = Math.floor(40 + Math.random() * 50);
  } else {
    playerData.velocity = Math.floor(50 + Math.random() * 40);
    playerData.control = Math.floor(40 + Math.random() * 50);
    playerData.movement = Math.floor(40 + Math.random() * 50);
  }
  
  if (isSupabaseConfigured() && supabase) {
    const { error } = await supabase.from('players').insert(playerData);
    if (error) {
      console.error(`❌ Player insert error for team ${teamId}:`, JSON.stringify(error));
    }
  }
}

// Random name helpers
const FIRST_NAMES = ['James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Charles', 'Mary', 'Patricia', 'Jennifer', 'Linda', 'Barbara', 'Elizabeth', 'Susan', 'Jessica', 'Sarah', 'Karen'];
const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin'];

function getRandomFirstName(): string {
  return FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
}

function getRandomLastName(): string {
  return LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
}

// Get current simulation day
export async function getCurrentDay(): Promise<number> {
  if (isSupabaseConfigured() && supabase) {
    const { data } = await supabase.from('simulation_state').select('current_day').eq('id', 'global').single();
    return data?.current_day || 0;
  }
  return 0;
}
